import { ElementType } from "react";

import {
  Box,
  IconButton,
  List as MUIList,
  ListItemButton as MUIListItemButton,
  Stack,
  styled,
} from "@mui/material";
import logoSrc from "assets/images/logo.png";
import shortLogoSrc from "assets/images/short-logo.png";
import { base, error } from "configs/colors";
import { lineHeight, rem } from "utils";
import { getPrimaryColor } from "utils/theme";

import AppDivider from "../Divider";

export const Container = styled(Box)<{
  collapsed: boolean;
  isMobile?: boolean;
}>(({ collapsed, isMobile }) => ({
  ".MuiPaper-root": {
    borderRight: 0,
    borderLeft: 0,
    // eslint-disable-next-line no-nested-ternary
    width: isMobile ? "0px !important" : collapsed ? "60px" : "280px",
    height: "100vh",
    backgroundColor: getPrimaryColor()[700],
    outline: 0,
    transition: isMobile
      ? ""
      : "width 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, background-color 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  },
}));

export const LogoWrapper = styled("a")(() => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  paddingLeft: rem(8),
  paddingRight: rem(8),
}));

export const Logo = styled("div")<{
  collapsed: boolean;
  logo?: string;
  shortLogo?: string;
}>(({ collapsed, logo, shortLogo }) => ({
  width: "100%",
  height: rem(34),
  cursor: "pointer",
  transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  backgroundImage: `url('${
    collapsed ? shortLogo || shortLogoSrc : logo || logoSrc
  }')`,
  backgroundPosition: "center",
  backgroundSize: "contain",
  backgroundRepeat: "no-repeat",
  borderRadius: "4px",
  "&:hover": {
    opacity: 0.7,
  },
}));

export const MenuContainer = styled(Box)(() => ({
  flex: 1,
  height: "100%",
  position: "relative",
  overflow: "hidden",
  marginTop: rem(-16),
  marginBottom: rem(-16),

  "&:hover .menu-scrollbar": {
    opacity: 1,
  },
}));

export const MenuWrapper = styled(Stack)(() => ({
  height: "inherit",
  gap: rem(4),
  justifyContent: "space-between",
  overflow: "hidden auto",
  paddingTop: rem(16),
  paddingBottom: rem(16),

  scrollbarWidth: "none" /* Firefox */,
  scrollbarColor: "transparent transparent" /* Firefox */,

  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

export const MenuBarPlaceholder = styled(Box)(() => ({
  display: "var(--menu-bar-placeholder-display)",
  width: "100vw",
  height: "100vh",
  position: "fixed",
  zIndex: 100,
  inset: 0,
}));

export const MenuBarTrack = styled(Box)(() => ({
  "--menu-bar-placeholder-display": "none",
  "--menu-scrollbar-height": "0px",
  "--menu-scrollbar-top": "0px",
  position: "absolute",
  height: "inherit",
  overflow: "hidden",
  zIndex: 99,
  right: 0,
  bottom: 0,
  top: 0,
  width: rem(8),
}));

export const MenuScrollbar = styled(Box)(() => ({
  width: rem(6),
  height: "var(--menu-scrollbar-height, 0)",
  position: "absolute",
  borderRadius: "7px",
  zIndex: 99,
  top: 0,
  bottom: 0,
  right: 1,
  background: getPrimaryColor()[400],
  transition:
    "opacity 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms, background 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  opacity: 0,
  transform: "translate3d(0px, var(--menu-scrollbar-top, 0), 0px)",
}));

export const List = styled(MUIList)<{ component?: string }>(() => ({
  padding: `0px ${rem(8)}`,
  display: "flex",
  flexDirection: "column",
  rowGap: rem(4),
}));

export const SubList = styled(Box)<{
  level: number;
  numberOfChildren: number;
}>(({ level, numberOfChildren }) => ({
  display: "flex",
  flexDirection: "column",
  rowGap: rem(4),
  marginTop: rem(4),
  position: "relative",
  "&:before":
    numberOfChildren > 1
      ? {
          content: '""',
          position: "absolute",
          zIndex: 2,
          top: "50%",
          transform: "translateY(-50%)",
          left: rem(level * 12 + 8),
          width: "1px",
          height: `calc(100% - ${rem(24)})`,
          borderLeft: `1px dashed ${getPrimaryColor()[100]}`,
        }
      : "none",
}));

export const ListItemButton = styled(MUIListItemButton)<{
  to?: string;
  component?: ElementType;
  active?: boolean;
  open?: boolean;
  level: number;
  collapsed: boolean;
  isSubHasChild?: boolean;
}>(({ active, open, level, collapsed }) => {
  const getBgByLevel = () => {
    if (level === 1) {
      return getPrimaryColor()[950];
    }
    if (level === 2) {
      return getPrimaryColor()[900];
    }
    if (level === 3) {
      return getPrimaryColor()[800];
    }
    return getPrimaryColor()[950];
  };
  return {
    borderRadius: "8px",
    backgroundColor: active ? getBgByLevel() : "inherit",
    color: base.White,
    padding: `${rem(8)} ${rem(12)}`,
    paddingLeft: rem(level * 12),
    whiteSpace: "nowrap",
    overflow: "hidden",
    columnGap: rem(12),
    "&:hover": {
      backgroundColor: getPrimaryColor()[600],
    },
    ".MuiListItemText-root": {
      opacity: collapsed ? 0 : 1,
      margin: 0,
      transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
      "& .MuiTypography-root": {
        fontSize: rem(14),
        lineHeight: rem(20),
        fontWeight: 600,
      },
    },
    ".MuiListItemIcon-root": {
      display: "grid",
      placeItems: "center",
      minWidth: rem(20),
      minHeight: rem(20),
      color: base.White,
      "& > .MuiSvgIcon-root": {
        fill: "none",
      },
    },
    ".MuiListItemText-root + .MuiSvgIcon-root": {
      opacity: collapsed ? 0 : 1,
      fontSize: rem(14),
      lineHeight: rem(20),
      fill: "none",
      color: base.White,
      transform: open ? "rotate(90deg)" : "none",
      transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    },
  };
});

export const UserWrapper = styled(Box)(() => ({
  marginLeft: rem(8),
  marginRight: rem(8),
  paddingLeft: rem(2),
  paddingRight: rem(2),
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  columnGap: rem(12),
  "& .MuiBadge-root .MuiBadge-badge": {
    display: "block",
    textAlign: "center",
    boxShadow: `0 0 0 1px ${base.White}`,
    "&::after": {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      borderRadius: "50%",
      border: `1px solid ${error[600]}`,
      content: '""',
    },
  },
  "& .MuiBadge-root:not(.collapsed) .MuiBadge-badge": {
    fontSize: rem(10),
    lineHeight: lineHeight(14),
    fontWeight: 600,
    height: "15px",
    minWidth: "15px",
    padding: "0px 3px",
  },
  "& .MuiBadge-root.collapsed .MuiBadge-badge": {
    color: error[600],
    "&:after": {
      animation: "ripple 1.2s infinite ease-in-out",
    },
  },
  "@keyframes ripple": {
    "0%": {
      transform: "scale(.8)",
      opacity: 1,
    },
    "100%": {
      transform: "scale(2)",
      opacity: 0,
    },
  },
}));

export const UserInfo = styled(Box)<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    flex: 1,
    width: collapsed ? "0px" : "unset",
    opacity: collapsed ? 0 : 1,
    transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
    display: "flex",
    flexDirection: "column",

    ".MuiTypography-root": {
      display: "-webkit-box",
      overflow: "hidden",
      textOverflow: "ellipsis",
      "-webkit-box-orient": "vertical",
      "-webkit-line-clamp": "1",
      fontSize: rem(14),
      lineHeight: lineHeight(20),

      "&:first-of-type": {
        color: base.White,
        fontWeight: 600,
      },
      "&:last-of-type": {
        color: getPrimaryColor()[200],
      },
    },
  })
);

export const LogoutButton = styled(IconButton)<{ collapsed: boolean }>(
  ({ collapsed }) => ({
    width: rem(36),
    height: rem(36),
    flexBasis: rem(36),
    color: getPrimaryColor()[300],
    opacity: collapsed ? 0 : 1,
    transition: "all 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
  })
);

export const Divider = styled(AppDivider)(() => ({
  backgroundImage:
    "linear-gradient(to right, rgba(52, 71, 103, 0), rgb(255, 255, 255), rgba(52, 71, 103, 0)) !important",
}));

export const Flag = styled("img")(() => ({
  maxWidth: rem(20),
  maxHeight: rem(20),
  width: "100%",
  height: "100%",
}));
